import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { salesforceApi } from '@/api/salesforce';
import { queryKeys } from '@/lib/query-keys';
import { useTenantId } from '@/providers/TenantProvider';
import type { UUID } from '@/types';

export function useSalesforceOrgs() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.orgs(tenantId),
    queryFn: () => salesforceApi.listOrgs(tenantId),
    enabled: !!tenantId,
  });
}

export function useSalesforceOrg(orgId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.org(tenantId, orgId),
    queryFn: () => salesforceApi.getOrg(tenantId, orgId),
    enabled: !!tenantId && !!orgId,
  });
}

export function useSalesforceAuth(orgId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.auth(tenantId, orgId),
    queryFn: () => salesforceApi.getAuth(tenantId, orgId),
    enabled: !!tenantId && !!orgId,
    retry: false,
  });
}

export function useTestSalesforceAuth(orgId: UUID) {
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: () => salesforceApi.testAuth(tenantId, orgId),
  });
}

export function useDiscoverQueues(orgId: UUID, enabled = true) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.salesforce.discovery(tenantId, orgId, 'queues'),
    queryFn: () => salesforceApi.discoverQueues(tenantId, orgId),
    enabled: !!tenantId && !!orgId && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSyncSalesforceOrg(orgId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => salesforceApi.syncFromSalesforce(tenantId, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salesforce.org(tenantId, orgId) });
    },
  });
}
